import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	FileWithShare,
	isShared,
	wasShared,
} from "@/lib/supabase/supabaseTypes";
import { useStorage } from "./useStorage";
import { useCentralStore } from "../store/Central";
import { useJobStore } from "../store/Jobs";
import { useRecycleBinStore } from "../store/RecycleBin";
import { useAccountingStore } from "../store/Accounting";
import { isInvoiceFile, isSpecificFile } from "../store/RecycleBin/utils";
import { showNotification } from "../store/Central/selectors";
import { supabase } from "@/lib/supabase/supabaseClient";
import { PdfTemplateType } from "../pdf-templates/document-template.types";
import { TableInvoiceRow } from "../pages/debitoren/types/invoice-table.types";
import { JobDocumentWithFiles } from "../store/Jobs/job-documents.store";
import posthog from "posthog-js";

interface UseFileActionsParams {
	file: FileWithShare;
	jobDocument?: JobDocumentWithFiles;
	options?: {
		blockShareButtons?: boolean;
		blockDelete?: boolean;
	};
}

export const useFileActions = ({
	file,
	jobDocument,
	options,
}: UseFileActionsParams) => {
	const [openShareDialog, setOpenShareDialog] = useState(false);
	const [openUnshareDialog, setOpenUnshareDialog] = useState(false);
	const [openCancelInvoiceDialog, setOpenCancelInvoiceDialog] =
		useState(false);
	const [cancellationLoading, setCancellationLoading] = useState(false);
	const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(
		null
	);
	const [invoiceData, setInvoiceData] = useState<TableInvoiceRow | null>(
		null
	);

	const { openFileInNewTab, downloadFromSupabase } = useStorage();
	const navigate = useNavigate();

	const { connectedOrganizationEmail } = useCentralStore((state) => ({
		connectedOrganizationEmail: state.connectedOrganizationEmail,
	}));

	const { shareFile, unshareFile, fetchJobDocument, fetchJob } = useJobStore(
		(state) => ({
			shareFile: state.shareFile,
			unshareFile: state.unshareFile,
			fetchJobDocument: state.fetchJobDocument,
			fetchJob: state.fetchJob,
		})
	);

	const { cancelInvoice, fetchAkontoView } = useAccountingStore((state) => ({
		cancelInvoice: state.cancelInvoice,
		fetchAkontoView: state.fetchAkontoView,
	}));

	const { deleteFileTemporarily } = useRecycleBinStore((state) => ({
		deleteFileTemporarily: state.deleteFileTemporarily,
	}));

	const handleShare = () => {
		if (!file.id) return;
		posthog.capture("share_file", {
			file_name: file.file_name,
		});
		posthog.capture("file_shared_with_org", {
			file_name: file.file_name,
			document_type: jobDocument?.type ?? null,
			recipient_org_type: null,
		});
		const orgId = useCentralStore.getState().organization?.id;
		if (orgId) {
			posthog.group("organization", orgId, {
				$set_once: { first_file_shared_at: new Date().toISOString() },
			});
		}
		if (jobDocument) {
			shareFile(file.id, jobDocument.id);
		} else {
			shareFile(file.id);
		}
	};

	const handleUnshare = () => {
		if (!file.id) return;
		posthog.capture("unshare_file", {
			file_name: file.file_name,
		});
		if (jobDocument) {
			unshareFile(file.id, jobDocument.id);
		} else {
			unshareFile(file.id);
		}
	};

	const fetchInvoicePaymentData = async () => {
		if (!file.id) return;

		const { data, error } = await supabase
			.from("invoice_with_payments_view")
			.select("invoice_id, payments")
			.eq("file_id", file.id)
			.single();

		if (error || !data) {
			showNotification({
				message: "Fehler beim Laden der Rechnungsdaten",
				type: "error",
			});
			return;
		}

		setSelectedInvoiceId(data.invoice_id);
		setInvoiceData(data as TableInvoiceRow);
		setOpenCancelInvoiceDialog(true);
	};

	const handleCancelInvoice = async (
		reason: string,
		rebookAsAkonto: boolean,
		cancelReminderFees: boolean
	) => {
		if (!selectedInvoiceId || !jobDocument) return;

		setCancellationLoading(true);
		try {
			await cancelInvoice(
				selectedInvoiceId,
				reason,
				rebookAsAkonto,
				cancelReminderFees
			);
			await fetchJobDocument(jobDocument.id);

			if (jobDocument.job_id) {
				await fetchJob(jobDocument.job_id);
			}

			if (rebookAsAkonto) {
				fetchAkontoView();
			}

			setOpenCancelInvoiceDialog(false);
		} catch {
			showNotification({
				message: "Fehler beim Stornieren der Rechnung",
				type: "error",
			});
		} finally {
			setCancellationLoading(false);
			setSelectedInvoiceId(null);
			setInvoiceData(null);
		}
	};

	const handleCancelInvoiceDialogClose = () => {
		setOpenCancelInvoiceDialog(false);
		setSelectedInvoiceId(null);
		setInvoiceData(null);
	};

	const handleDelete = async () => {
		if (!file.id) return;
		if (await deleteFileTemporarily(file)) {
			showNotification({
				message:
					"Die Datei wurde erfolgreich in den Papierkorb verschoben",
				action: {
					label: "Papierkorb anzeigen",
					onClick: () => {
						navigate("/recycle-bin");
					},
				},
				type: "success",
			});
		}
	};

	const handleDownload = () => {
		posthog.capture("file_downloaded", {
			file_type: file.file_name?.split(".").pop() ?? "unknown",
			is_shared_file: isShared(file),
			source_app:
				typeof __SOURCE_APP__ !== "undefined"
					? __SOURCE_APP__
					: "dentlab",
		});
		if (isShared(file)) {
			const orgId = useCentralStore.getState().organization?.id;
			if (orgId) {
				posthog.group("organization", orgId, {
					$set_once: {
						first_file_downloaded_at: new Date().toISOString(),
					},
				});
			}
		}
		downloadFromSupabase(file.path_name as string);
	};

	const handleOpenFileInNewTab = () => {
		posthog.capture("file_downloaded", {
			file_type: file.file_name?.split(".").pop() ?? "unknown",
			is_shared_file: isShared(file),
			source_app:
				typeof __SOURCE_APP__ !== "undefined"
					? __SOURCE_APP__
					: "dentlab",
		});
		if (isShared(file)) {
			const orgId = useCentralStore.getState().organization?.id;
			if (orgId) {
				posthog.group("organization", orgId, {
					$set_once: {
						first_file_downloaded_at: new Date().toISOString(),
					},
				});
			}
		}
		openFileInNewTab(file.path_name as string);
	};

	const isFileShared = isShared(file);
	const wasFileShared = wasShared(file);
	const canShare =
		!options?.blockShareButtons && !!connectedOrganizationEmail;
	const canDelete = !isInvoiceFile(file) && !options?.blockDelete;

	const shareColor: "success" | "error" | undefined = (() => {
		if (file.invoice_canceled_at) return "error";
		if (options?.blockShareButtons) return undefined;
		if (!wasFileShared) return undefined;
		if (!file.shared_until || new Date(file.shared_until) > new Date()) {
			return "success";
		}
		return "error";
	})();

	const canCancelInvoice =
		isSpecificFile(file, [
			PdfTemplateType.RECHNUNG,
			PdfTemplateType.BARZAHLUNGSQUITTUNG,
			PdfTemplateType.KARTENZAHLUNGSQUITTUNG,
		]) &&
		!!file.file_name &&
		!file.invoice_canceled_at;

	return {
		openShareDialog,
		setOpenShareDialog,
		openUnshareDialog,
		setOpenUnshareDialog,
		openCancelInvoiceDialog,
		setOpenCancelInvoiceDialog,
		cancellationLoading,
		invoiceData,
		handleShare,
		handleUnshare,
		handleDelete,
		handleDownload,
		handleOpenFileInNewTab,
		fetchInvoicePaymentData,
		handleCancelInvoice,
		handleCancelInvoiceDialogClose,
		connectedOrganizationEmail,
		canShare,
		canDelete,
		canCancelInvoice,
		isFileShared,
		wasFileShared,
		shareColor,
	};
};
